import {Fragment, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import type {ReactElement, PropsWithChildren, KeyboardEvent, FocusEvent} from 'react'
import clsx from 'clsx'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {noop} from '@github-ui/noop'
import type {TreeViewSubTreeProps} from '@primer/react'
import {Box, TreeView, merge} from '@primer/react'
import {ScopedCommands} from '@github-ui/ui-commands'

import {useNestedListViewProperties} from '../context/NestedListViewPropertiesContext'
import {useNestedListViewSelection} from '../context/SelectionContext'
import {useNestedListViewItems} from '../context/ItemsContext'

import {ActionsProvider} from './context/ActionsContext'
import {NestedListItemMetadataContainer, type MetadataContainerProps} from './MetadataContainer'
import {NestedListItemSelectionProvider, useNestedListItemSelection} from './context/SelectionContext'
import {NestedListItemTitleProvider, useNestedListItemTitle} from './context/TitleContext'
import {NestedListItemDescriptionProvider, useNestedListItemDescription} from './context/DescriptionContext'
import {NestedListItemStatusProvider, useNestedListItemStatus} from './context/StatusContext'
import {NestedListItemActionBar} from './ActionBar'
import type {NestedListItemTitle} from './Title'
import {NestedListItemLeadingBadgeProvider, useNestedListItemLeadingBadge} from './context/LeadingBadgeContext'
import {NestedListItemNewActivityProvider, useNestedListItemNewActivity} from './context/NewActivityContext'
import {NestedListItemSelection} from './Selection'
import {ControlsDialogHint} from './ControlsDialogHint'

export type NestedListItemProps = PropsWithChildren<{
  isSelected?: boolean
  isActive?: boolean
  onSelect?: (isSelected: boolean) => void
  /*
   * Optional. Overrides the default behavior of the list item when a key is pressed
   */
  onKeyDown?: (event: KeyboardEvent<HTMLLIElement>) => void
  onFocus?: (event: FocusEvent<HTMLLIElement>) => void
  /**
   * Called when the item's controls dialog should be opened. This should trigger a dialog that contains alternative
   * means to perform _all_ secondary actions on this list item.
   *
   * Important: Focus must be returned to this list item when the dialog is closed.
   */
  onOpenControlsDialog?: () => void

  /**
   * A NestedListItem title communicates the overall purpose of the NestedListItem.
   */
  title: ReactElement<typeof NestedListItemTitle>

  /**
   * Optional extra elements to display on the right side of the list item. You can optionally wrap individual
   * pieces of content in `NestedListItem.Metadata` for a consistent appearance.
   */
  metadata?: MetadataContainerProps['children']

  /**
   * Optional menu of additional actions to be shown on the right side of the NestedListItem. Use `NestedListItem.ActionBar`.
   */
  secondaryActions?: ReactElement<typeof NestedListItemActionBar>
  sx?: BetterSystemStyleObject
  metadataContainerSx?: BetterSystemStyleObject
  /**
   * Optional. Show an expand icon to indicating that the list item has additional sub items to show.
   */
  subItemsCount?: number
  /**
   * Optional. Nested sub items that will be rendered when the sub tree is expanded.
   */
  subItems?: Array<ReactElement<typeof NestedListItem>>

  /**
   * Optional. Callback that returns sub items to render.
   */
  loadAdditionalSubItems?: () => Promise<Array<ReactElement<typeof NestedListItem>>>
}>

// by default merge (via deepmerge) combines arrays by concatenating them
// this creates unexpected behavior when merging sx props (such as display)
// this util allows us more fine-grained control over how arrays are merged
const preferIncomingArray = (destinationArray: unknown[], sourceArray: unknown[]) =>
  sourceArray.length > 0 ? sourceArray : destinationArray

// minmax(0, max-content) - '0' is required to fix overflow caused by the "metadata" on smaller screen sizes
// 2em for short titles and description to be aligned next to title
const commonDefaultGridTemplateColumns =
  'min-content min-content minmax(2em, max-content) minmax(0, auto) minmax(min-content, max-content)'
const commonNestedListItemSx: BetterSystemStyleObject = {
  position: 'relative',
  display: 'grid',
  minHeight: '1rem',
  fontSize: 1,
  gap: 1,
  color: 'fg.default',
  // The margin accounts for a UI bug where the list item hover would block out the keyboard item focus box shadow
  margin: '2px 0 2px 0',
  // this is to mimic what subgrid will do for us, which is
  // to create a placeholder for metadata items that might still be empty (but hide them on mobile if they are empty)
  '.listitem-metadata:empty': {
    display: ['none', 'flex'],
  },

  '&:hover': {
    backgroundColor: 'canvas.subtle',
  },

  '&.nested-listviewitem-selected': {
    backgroundColor: 'accent.subtle',
    boxShadow: '0 0 0 1px var(--borderColor-accent-emphasis, var(--color-accent-fg))',
    zIndex: 1,

    '&:hover': {
      backgroundColor: 'accent.subtle',
    },
  },

  '&:focus-visible': {
    outline: '2px solid var(--focus-outlineColor, var(--color-accent-fg))',
    outlineOffset: '-2px',
    boxShadow: 'none',
  },
}

const defaultTwoRow = '"selection status primary main-content actions" "selection status metadata metadata actions"'
const defaultOneRow = '"selection status primary main-content metadata actions"'

const defaultVariantNestedListItemSx: BetterSystemStyleObject = {
  // xsmall: 2 rows, small+: 1 row
  // https://primer.style/foundations/layout#breakpoints
  gridTemplateRows: ['repeat(2,auto)', 'auto'],
  gridTemplateAreas: [defaultTwoRow, defaultOneRow],
  gridTemplateColumns: `${commonDefaultGridTemplateColumns} min-content`,
}

const NestedListItemInternal = ({
  children,
  isActive = false,
  title,
  metadata,
  secondaryActions,
  sx: externalNestedListItemSx,
  metadataContainerSx,
  subItemsCount,
  subItems: externalSubItems,
  loadAdditionalSubItems,
  onOpenControlsDialog,
  ...props
}: Omit<NestedListItemProps, 'isSelected' | 'onSelect'>): JSX.Element => {
  const {idPrefix} = useNestedListViewProperties()
  // const {isSelectable} = useNestedListViewSelection()
  const {anyItemsWithActionBar, hasResizableActionsWithActionBar} = useNestedListViewItems()
  const uniqueIdSuffix = useId()
  const {isSelected} = useNestedListItemSelection()
  const {status: labelStatus} = useNestedListItemStatus()
  const {title: labelTitle} = useNestedListItemTitle()
  const {description: labelDescription} = useNestedListItemDescription()
  const {leadingBadge: labelLeadingBadge} = useNestedListItemLeadingBadge()
  const {hasNewActivity} = useNestedListItemNewActivity()
  const itemRef = useRef<HTMLLIElement>(null)
  const [subItems, setSubItems] = useState(externalSubItems)
  const [loadingError, setLoadingError] = useState('An error occured while loading items')
  const [subItemsLoadingState, setSubItemsLoadingState] = useState<TreeViewSubTreeProps['state']>('initial')
  // This allows the actions to show in the action bar depending on the screen size. These actions
  // will move into a dropdown menu when the screen isn't big enough to fit them side by side.
  // Edge case: if some items have resizable actions and some don't have an actionbar,
  // column alignment will be off.
  const gridTemplateSx =
    anyItemsWithActionBar && hasResizableActionsWithActionBar
      ? {gridTemplateColumns: `${commonDefaultGridTemplateColumns} minmax(0, auto)`}
      : {}

  const hasSubItems = subItemsCount && subItemsCount > 0
  const baseNestedListItemSx = merge<BetterSystemStyleObject>(commonNestedListItemSx, defaultVariantNestedListItemSx)

  const flexibleNestedListItemSx: BetterSystemStyleObject = merge<BetterSystemStyleObject>(
    baseNestedListItemSx,
    gridTemplateSx,
  )
  const listItemSx = externalNestedListItemSx
    ? merge<BetterSystemStyleObject>(flexibleNestedListItemSx, externalNestedListItemSx, {
        arrayMerge: preferIncomingArray,
      })
    : flexibleNestedListItemSx

  useEffect(() => {
    if (!itemRef.current) return
    // Focus the element unless another element in the document is specifically focused
    if (isActive && document.activeElement?.tagName === 'BODY') {
      itemRef.current.focus()
    }
  })

  // Enter = primary action (link)
  // Space = Check/uncheck(if selection is enabled), otherwise navigate to the link
  // TODO: currently the space keystroke is not supported in the primer treeview component
  // const onKeyDown = useCallback(
  //   (e: KeyboardEvent<HTMLLIElement>) => {
  //     // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  //     switch (e.key) {
  //       case 'Enter':
  //         if (titleAction) titleAction(e)
  //         break
  //       case ' ':
  //         // When the user presses Space
  //         if (!isSelectable) break
  //         if (itemRef?.current !== document.activeElement) break // if the focus is inside the listitem, do not select the item
  //         e.preventDefault()
  //         onSelect(!isSelected)
  //         break
  //       case 'Escape':
  //         itemRef?.current?.focus()
  //         break
  //       default:
  //         break
  //     }
  //   },
  //   [titleAction, isSelectable, onSelect, isSelected],
  // )

  const getAriaLabel = useCallback(() => {
    const labelSelected = isSelected ? 'Selected' : ''
    const labelNewActivity = hasNewActivity ? 'New activity' : ''
    const hasSecondaryActions = !!secondaryActions
    const hasMetadata = (Array.isArray(metadata) && metadata.length > 0) || (metadata && !Array.isArray(metadata))
    let labelAdditionalInfo = ''
    if (hasMetadata || hasSecondaryActions) {
      labelAdditionalInfo = 'Press Control, Shift, U for more actions'
    }

    const main = [labelLeadingBadge, labelTitle, labelStatus]
      .filter(str => str.trim())
      .join(': ')
      .replace(/\.+$/, '') // Remove additional periods
    const ariaLabel = [labelSelected, main, labelDescription, labelNewActivity, labelAdditionalInfo]
      .filter(str => str.trim())
      .join('. ')

    return ariaLabel.endsWith('.') ? ariaLabel : `${ariaLabel}.`
  }, [
    secondaryActions,
    metadata,
    isSelected,
    hasNewActivity,
    labelLeadingBadge,
    labelTitle,
    labelStatus,
    labelDescription,
  ])

  useEffect(() => {}, [metadata, secondaryActions])

  const loadItems = useCallback(async () => {
    try {
      setSubItemsLoadingState('loading')
      const subIssues = await loadAdditionalSubItems?.()
      setSubItems(subIssues)
      setSubItemsLoadingState('done')
    } catch (error) {
      setSubItemsLoadingState('error')
      if (error instanceof Error) {
        setLoadingError(error.message)
      }
    }
  }, [loadAdditionalSubItems, setSubItems, setSubItemsLoadingState])

  return (
    <ScopedCommands
      commands={{
        'list-views:open-manage-item-dialog': onOpenControlsDialog,
      }}
    >
      <TreeView.Item
        onSelect={noop}
        onExpandedChange={() => {
          if (hasSubItems && !subItems) {
            loadItems()
          }
        }}
        ref={itemRef}
        id={`${idPrefix}-listview-node-${uniqueIdSuffix}`}
        aria-label={getAriaLabel()}
        {...props}
      >
        <Box
          id={`${idPrefix}-listview-node-${uniqueIdSuffix}`}
          className={clsx({'nested-listviewitem-selected': isSelected}, 'nested-listview-item-node')}
          tabIndex={-1} // Handled by useFocusZone with roving tabIndex
          sx={listItemSx}
          // onKeyDown={onKeyDown}
        >
          {title}
          <NestedListItemSelection />
          {children}
          {Array.isArray(metadata) && metadata.length > 0 ? (
            <NestedListItemMetadataContainer sx={metadataContainerSx}>
              {metadata.map((metadataItem, index) => (
                <Fragment key={index}>{metadataItem}</Fragment>
              ))}
            </NestedListItemMetadataContainer>
          ) : (
            metadata &&
            !Array.isArray(metadata) && (
              <NestedListItemMetadataContainer sx={metadataContainerSx}>{metadata}</NestedListItemMetadataContainer>
            )
          )}
          {secondaryActions ?? (anyItemsWithActionBar && <NestedListItemActionBar />)}
        </Box>

        {onOpenControlsDialog && <ControlsDialogHint />}

        {(hasSubItems || subItems) && (
          <TreeView.SubTree state={subItemsLoadingState} count={subItemsCount}>
            {subItemsLoadingState === 'error' ? (
              <TreeView.ErrorDialog
                onRetry={() => {
                  setSubItemsLoadingState('initial')
                  loadItems()
                }}
                onDismiss={() => {
                  setSubItemsLoadingState('initial')
                }}
              >
                {loadingError}
              </TreeView.ErrorDialog>
            ) : null}
            {subItems}
          </TreeView.SubTree>
        )}
      </TreeView.Item>
    </ScopedCommands>
  )
}

export const NestedListItem = ({
  children,
  isSelected = false,
  onSelect = noop,
  ...rest
}: PropsWithChildren<NestedListItemProps>): JSX.Element => {
  const {setSelectedCount} = useNestedListViewSelection()
  const isSelectedRef = useRef(isSelected)
  isSelectedRef.current = isSelected

  useEffect(() => {
    setSelectedCount(count => (isSelected ? count + 1 : Math.max(0, count - 1)))
  }, [isSelected, setSelectedCount])

  useEffect(() => {
    return () => {
      if (isSelectedRef.current) {
        setSelectedCount(count => Math.max(0, count - 1))
      }
    }
  }, [setSelectedCount])

  const selectionProviderProps = useMemo(() => ({isSelected, onSelect}), [isSelected, onSelect])

  return (
    <ActionsProvider>
      <NestedListItemLeadingBadgeProvider>
        <NestedListItemTitleProvider>
          <NestedListItemSelectionProvider value={selectionProviderProps}>
            <NestedListItemNewActivityProvider>
              <NestedListItemDescriptionProvider>
                <NestedListItemStatusProvider>
                  <NestedListItemInternal {...rest}>{children}</NestedListItemInternal>
                </NestedListItemStatusProvider>
              </NestedListItemDescriptionProvider>
            </NestedListItemNewActivityProvider>
          </NestedListItemSelectionProvider>
        </NestedListItemTitleProvider>
      </NestedListItemLeadingBadgeProvider>
    </ActionsProvider>
  )
}

NestedListItem.displayName = 'NestedListItem'

try{ NestedListItemInternal.displayName ||= 'NestedListItemInternal' } catch {}